import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const QuoteItemForm = ({ onAddItem, suppliers = [], editingItem }) => {
  const [formData, setFormData] = useState({
    type: '',
    description: '',
    quantity: 1,
    cost: '',
    price: '',
    supplier: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editingItem) {
      setFormData({
        type: editingItem?.type || '',
        description: editingItem?.description || '',
        quantity: editingItem?.quantity || 1,
        cost: editingItem?.cost !== undefined ? String(editingItem?.cost) : '',
        price: editingItem?.price !== undefined ? String(editingItem?.price) : '',
        supplier: editingItem?.supplier || ''
      });
    }
  }, [editingItem]);

  const itemTypes = [
    { value: 'service', label: 'Servicio' },
    { value: 'part', label: 'Refacción' },
    { value: 'tire', label: 'Llanta' },
    { value: 'labor', label: 'Mano de Obra' },
    { value: 'other', label: 'Otro' }
  ];

  const supplierOptions = suppliers?.map(s => ({
    value: s?.id,
    label: s?.name,
    description: s?.category
  }));

  const calculateMargin = () => {
    const cost = parseFloat(formData?.cost) || 0;
    const price = parseFloat(formData?.price) || 0;
    if (cost === 0) return 0;
    return ((price - cost) / cost * 100)?.toFixed(2);
  };

  const margin = calculateMargin();
  const marginColor = margin >= 30 ? 'text-success' : margin >= 15 ? 'text-warning' : 'text-error';

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.type) newErrors.type = 'Seleccione un tipo';
    if (!formData?.description?.trim()) newErrors.description = 'Ingrese una descripción';
    if (formData?.quantity < 1) newErrors.quantity = 'La cantidad debe ser mayor a 0';
    if (!formData?.cost || parseFloat(formData?.cost) <= 0) newErrors.cost = 'Ingrese un costo válido';
    if (!formData?.price || parseFloat(formData?.price) <= 0) newErrors.price = 'Ingrese un precio válido';
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onAddItem({
        ...formData,
        id: Date.now(),
        cost: parseFloat(formData?.cost),
        price: parseFloat(formData?.price),
        quantity: parseInt(formData?.quantity),
        margin: parseFloat(margin),
        addedAt: new Date()?.toISOString()
      });
      setFormData({
        type: '',
        description: '',
        quantity: 1,
        cost: '',
        price: '',
        supplier: ''
      });
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4 md:p-6">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="Plus" size={24} className="text-primary" />
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
          Agregar Item Manual
        </h2>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Tipo de Item"
            required
            options={itemTypes}
            value={formData?.type}
            onChange={(value) => handleChange('type', value)}
            error={errors?.type}
            placeholder="Seleccione tipo"
          />

          <Select
            label="Proveedor Sugerido"
            options={supplierOptions}
            value={formData?.supplier}
            onChange={(value) => handleChange('supplier', value)}
            placeholder="Seleccione proveedor (opcional)"
            searchable
            clearable
          />
        </div>

        <Input
          label="Descripción"
          type="text"
          required
          value={formData?.description}
          onChange={(e) => handleChange('description', e?.target?.value)}
          error={errors?.description}
          placeholder="Ej: Cambio de aceite sintético 5W-30"
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Cantidad"
            type="number"
            required
            min="1"
            value={formData?.quantity}
            onChange={(e) => handleChange('quantity', e?.target?.value)}
            error={errors?.quantity}
          />

          <Input
            label="Costo Unitario (MXN)"
            type="number"
            required
            min="0"
            step="0.01"
            value={formData?.cost}
            onChange={(e) => handleChange('cost', e?.target?.value)}
            error={errors?.cost}
            placeholder="0.00"
          />

          <Input
            label="Precio Unitario (MXN)"
            type="number"
            required
            min="0"
            step="0.01"
            value={formData?.price}
            onChange={(e) => handleChange('price', e?.target?.value)}
            error={errors?.price}
            placeholder="0.00"
          />
        </div>

        {formData?.cost && formData?.price && (
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm caption text-muted-foreground">Margen de Ganancia:</span>
              <span className={`text-lg font-heading font-bold ${marginColor} data-text`}>
                {margin}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm caption text-muted-foreground">Costo Total:</span>
              <span className="text-sm font-body font-semibold text-foreground data-text">
                ${(parseFloat(formData?.cost || 0) * parseInt(formData?.quantity || 1))?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm caption text-muted-foreground">Precio Total:</span>
              <span className="text-sm font-body font-semibold text-foreground data-text">
                ${(parseFloat(formData?.price || 0) * parseInt(formData?.quantity || 1))?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
              </span>
            </div>
          </div>
        )}

        <Button
          type="submit"
          variant="default"
          iconName={editingItem ? 'Save' : 'Plus'}
          iconPosition="left"
          fullWidth
        >
          {editingItem ? 'Guardar cambios' : 'Agregar Item a Cotización'}
        </Button>
      </form>
    </div>
  );
};

export default QuoteItemForm;